import React, { useEffect, useState } from "react";
import styled from "styled-components";
import PropTypes from "prop-types";
import {
  Checkbox,
  FormControlLabel,
  Grid,
  RadioGroup,
  TextField,
  Typography,
  MenuItem,
  Select,
  InputLabel,
  FormControl
} from "@material-ui/core";
import { useForm, Controller } from "react-hook-form";
import Alert from "@material-ui/lab/Alert";
import Radio from "@material-ui/core/Radio";
import Autocomplete from "@material-ui/lab/Autocomplete";
import Dialog from "../../../waybee-ui/Dialog";
import Button from "../../../waybee-ui/Button";
import IconPicker from "../../../components/IconPicker";
import PreviewDialog from "./PreviewDialog";
import SchoolGroupService from "../../../services/SchoolGroupService";

const ContentGrid = styled(Grid)`
  margin: 10px -20px;
  padding: 45px 65px 60px 65px;
  max-height: 90vh;
  overflow-y: auto;
`;

const Label = styled.p`
  font-family: Roboto, sans-serif;
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 4px;
  margin-top: 15px;
`;

const IFrameLink = styled(Typography)`
  display: inline-block;
  padding: 0;
  margin: 0;
  color: blue;
  text-decoration: underline;
  cursor: pointer;
`;

const LinksDialog = ({ open, onClose, onSave, values }) => {
  const [showIFrame, setShowIFrame] = useState(false);
  const [linkWithVariable, setLinkWithVariable] = useState(null);
  const [variable, setVariable] = useState(null);
  const [allCourses, setAllCourses] = useState(true);
  const [coursesAndClasses, setCoursesAndClasses] = useState([]);
  const [schoolYears, setSchoolYears] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState("Turma");
  const [filterTypes] = useState(["Turma", "Série"]);
  const [error, setError] = useState("");

  const { register, getValues, watch, control, reset, setValue } = useForm({
    defaultValues: {
      public: true,
      allCourses: true,
      studentCourses: [],
      studentClasses: [],
      studentSeries: [],
      schoolYears: [],
      destinationStudent: true,
      destinationFinantial: false,
      destinationPedagogical: false,
      inAppLink: false,
      menu: "studentMenu"
    }
  });

  const showDestination = !watch("public");
  const hasInAppLink = watch("inAppLink");
  const link = watch("link");
  const menu = watch("menu");
  const studentCourses = watch("studentCourses") || [];
  const destinationStudent = watch("destinationStudent");
  const destinationFinantial = watch("destinationFinantial");
  const destinationPedagogical = watch("destinationPedagogical");

  useEffect(() => {
    if (
      !showDestination ||
      destinationStudent ||
      destinationFinantial ||
      destinationPedagogical
    ) {
      setError("");
    }
  }, [
    showDestination,
    destinationStudent,
    destinationFinantial,
    destinationPedagogical
  ]);

  const getStudentSeries = () => {
    let studentSeries = [];
    if (studentCourses && studentCourses.length) {
      studentCourses.forEach(course => {
        if (course && course.VW_SERIES) {
          studentSeries = [...studentSeries, ...course.VW_SERIES];
        }
      });
    }
    return studentSeries;
  };

  const getClassesByCourses = (courses, series) => {
    let classes = [];
    if (courses && courses.length) {
      courses.forEach(course => {
        if (course.VW_TURMA) {
          let filteredClasses = course.VW_TURMA;
          if (series && series.length) {
            filteredClasses = filteredClasses.filter(
              classItem =>
                series.map(serie => serie.CODIGO).indexOf(classItem.SERIE) >= 0
            );
          }
          classes = [...classes, ...filteredClasses];
        }
      });
    }
    return classes;
  };

  const getCoursesAndClasses = async year => {
    try {
      const response = await SchoolGroupService.getCoursesAndClasses(year);
      setCoursesAndClasses(response || []);
    } catch (e) {
      console.error("Error loading courses and classes:", e);
    }
  };

  const getSchoolYears = async () => {
    try {
      const response = await SchoolGroupService.getFilters();
      setSchoolYears(response?.schoolYear || []);
    } catch (e) {
      console.error("Error loading school years:", e);
    }
  };

  useEffect(() => {
    if (open) {
      getSchoolYears();
      const currentYear = new Date().getFullYear().toString();
      getCoursesAndClasses(currentYear);
    }
  }, [open]);

  const variableRegex = /\$\((.*?)\)/g;
  const displayPreview = variableRegex.test(link);

  const onSaveLink = () => {
    const formValues = getValues();

    if (
      !formValues.public &&
      !formValues.destinationStudent &&
      !formValues.destinationFinantial &&
      !formValues.destinationPedagogical
    ) {
      setError(
        "Selecione pelo menos um tipo de destinatário em 'Quem pode ver o link?'"
      );
      return;
    }

    setError("");
    onSave(formValues);
  };

  const handleVariableSubmit = () => {
    const urlVariable = link.replace(variableRegex, variable);
    setLinkWithVariable(urlVariable);
  };

  const onClosePreview = () => {
    setShowIFrame(false);
    setLinkWithVariable(null);
    setVariable("");
  };

  useEffect(() => {
    if (open) {
      if (values) {
        const allCoursesValue =
          values.allCourses === undefined ? true : values.allCourses;

        const resetValues = {
          ...values,
          allCourses: allCoursesValue,
          inAppLink: values.inAppLink !== undefined ? values.inAppLink : false
        };

        reset(resetValues);

        setAllCourses(allCoursesValue);
      } else {
        reset({
          name: "",
          icon: "link",
          link: "",
          public: true,
          allCourses: true,
          studentCourses: [],
          studentClasses: [],
          studentSeries: [],
          schoolYears: [],
          destinationStudent: true,
          destinationFinantial: false,
          destinationPedagogical: false,
          inAppLink: false
        });
        setAllCourses(true);
      }
      setError("");
    }
  }, [open, reset]);

  return (
    <Dialog open={open} onClose={onClose} closeButton maxWidth="md" fullWidth>
      <ContentGrid container spacing={2}>
        <Grid item xs={2} fullWidth>
          <Label>Ícone</Label>
          <Controller
            name="icon"
            control={control}
            defaultValue="link"
            render={({ field }) => <IconPicker {...field} />}
          />
        </Grid>

        <Grid item xs={10}>
          <Label>Texto do Botão</Label>
          <TextField
            {...register("name", { required: "Campo obrigatório" })}
            variant="outlined"
            fullWidth
          />
        </Grid>

        <Grid item xs={12}>
          <Label>Link</Label>
          <TextField
            {...register("link", { required: "Campo obrigatório" })}
            variant="outlined"
            fullWidth
          />
        </Grid>

        <Grid item xs={12}>
          <Controller
            name="menu"
            control={control}
            defaultValue="studentMenu"
            render={({ field }) => (
              <RadioGroup
                row
                {...field}
                onChange={e => {
                  field.onChange(e.target.value);
                  if (e.target.value === "sideMenu") {
                    setValue("public", true);
                  }
                }}
              >
                <FormControlLabel
                  value="sideMenu"
                  control={<Radio />}
                  label="Menu do responsável"
                />
                <FormControlLabel
                  value="studentMenu"
                  control={<Radio />}
                  label="Menu do aluno"
                />
              </RadioGroup>
            )}
          />
        </Grid>

        <Grid item xs={6}>
          <FormControlLabel
            control={(
              <Controller
                name="public"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    {...field}
                    checked={field.value}
                    onChange={e => field.onChange(e.target.checked)}
                    disabled={menu === "sideMenu"}
                  />
                )}
              />
            )}
            label="Link público"
          />
        </Grid>

        <Grid item xs={6}>
          <FormControlLabel
            control={(
              <Controller
                name="inAppLink"
                control={control}
                defaultValue={false}
                render={({ field }) => (
                  <Checkbox
                    {...field}
                    checked={field.value || false}
                    onChange={e => field.onChange(e.target.checked)}
                  />
                )}
              />
            )}
            label="Abrir link no app"
          />
        </Grid>

        {showDestination && (
          <Grid item xs={12}>
            <Label>Quem pode ver o link?</Label>
            <FormControlLabel
              control={(
                <Controller
                  name="destinationStudent"
                  control={control}
                  defaultValue
                  render={({ field }) => (
                    <Checkbox
                      {...field}
                      checked={field.value}
                      onChange={e => field.onChange(e.target.checked)}
                    />
                  )}
                />
              )}
              label="Alunos"
            />
            <FormControlLabel
              control={(
                <Controller
                  name="destinationFinantial"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      {...field}
                      checked={field.value}
                      onChange={e => field.onChange(e.target.checked)}
                    />
                  )}
                />
              )}
              label="Responsáveis Financeiros"
            />
            <FormControlLabel
              control={(
                <Controller
                  name="destinationPedagogical"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      {...field}
                      checked={field.value}
                      onChange={e => field.onChange(e.target.checked)}
                    />
                  )}
                />
              )}
              label="Responsáveis Pedagógico"
            />
          </Grid>
        )}

        {showDestination && (
          <Grid item xs={12}>
            <Label>Filtrar por Curso/Turma/Série</Label>
            <FormControlLabel
              style={{ marginBottom: "15px" }}
              control={(
                <Controller
                  name="allCourses"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      checked={field.value === undefined ? true : field.value}
                      onChange={e => {
                        field.onChange(e.target.checked);
                        setAllCourses(e.target.checked);
                        if (e.target.checked) {
                          setValue("schoolYears", []);
                          setValue("studentCourses", []);
                          setValue("studentClasses", []);
                          setValue("studentSeries", []);
                        }
                      }}
                    />
                  )}
                />
              )}
              label="Todos os cursos"
            />

            {!allCourses && (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="schoolYears"
                    control={control}
                    render={({ field }) => (
                      <Autocomplete
                        {...field}
                        multiple
                        options={schoolYears}
                        getOptionLabel={option => option?.toString() || ""}
                        value={field.value}
                        isOptionEqualToValue={(option, value) =>
                          option?.toString() === value?.toString()
                        }
                        onChange={(e, newValue) => {
                          field.onChange(newValue);
                          getCoursesAndClasses(newValue);
                        }}
                        renderInput={params => (
                          <TextField
                            {...params}
                            label="Ano letivo"
                            variant="outlined"
                            fullWidth
                          />
                        )}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Controller
                    name="studentCourses"
                    control={control}
                    render={({ field }) => (
                      <Autocomplete
                        {...field}
                        multiple
                        options={coursesAndClasses}
                        getOptionLabel={option =>
                          option?.NOME ||
                          (typeof option === "string" ? option : "")
                        }
                        value={field.value}
                        isOptionEqualToValue={(option, value) =>
                          option?.CODIGO === value?.CODIGO
                        }
                        onChange={(e, newValue) => {
                          field.onChange(newValue);
                          setValue("studentClasses", []);
                          setValue("studentSeries", []);
                        }}
                        renderInput={params => (
                          <TextField
                            {...params}
                            label="Curso"
                            variant="outlined"
                            fullWidth
                          />
                        )}
                      />
                    )}
                  />
                </Grid>

                {studentCourses && studentCourses.length > 0 && (
                  <>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth variant="outlined">
                        <InputLabel id="filter-type-label">
                          Filtrar por
                        </InputLabel>
                        <Select
                          labelId="filter-type-label"
                          value={selectedFilter}
                          onChange={e => {
                            setSelectedFilter(e.target.value);
                            if (e.target.value === "Turma") {
                              setValue("studentSeries", []);
                            } else {
                              setValue("studentClasses", []);
                            }
                          }}
                          label="Filtrar por"
                        >
                          {filterTypes.map(type => (
                            <MenuItem key={type} value={type}>
                              {type}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      {selectedFilter === "Série" ? (
                        <Controller
                          name="studentSeries"
                          control={control}
                          render={({ field }) => (
                            <Autocomplete
                              {...field}
                              multiple
                              options={getStudentSeries()}
                              getOptionLabel={option =>
                                option?.NOME ||
                                (typeof option === "string" ? option : "")
                              }
                              value={field.value}
                              isOptionEqualToValue={(option, value) =>
                                option?.CODIGO === value?.CODIGO
                              }
                              onChange={(e, newValue) => {
                                field.onChange(newValue);
                                const filteredClasses = getClassesByCourses(
                                  studentCourses,
                                  newValue
                                );
                                setValue("studentClasses", filteredClasses);
                              }}
                              renderInput={params => (
                                <TextField
                                  {...params}
                                  label="Série"
                                  variant="outlined"
                                  fullWidth
                                />
                              )}
                            />
                          )}
                        />
                      ) : (
                        <Controller
                          name="studentClasses"
                          control={control}
                          render={({ field }) => (
                            <Autocomplete
                              {...field}
                              multiple
                              options={getClassesByCourses(studentCourses)}
                              getOptionLabel={option =>
                                option?.NOME ||
                                (typeof option === "string" ? option : "")
                              }
                              value={field.value}
                              isOptionEqualToValue={(option, value) =>
                                option?.CODIGO === value?.CODIGO
                              }
                              onChange={(e, newValue) =>
                                field.onChange(newValue)
                              }
                              renderInput={params => (
                                <TextField
                                  {...params}
                                  label="Turma"
                                  variant="outlined"
                                  fullWidth
                                />
                              )}
                            />
                          )}
                        />
                      )}
                    </Grid>
                  </>
                )}
              </Grid>
            )}
          </Grid>
        )}

        {hasInAppLink && link && (
          <Grid item xs={12}>
            <Alert severity="warning">
              Alguns websites não permitem ser exibidos diretamente dentro do
              aplicativo. Para verificar a viabilidade do seu link, clique
              {" "}
              <IFrameLink onClick={() => setShowIFrame(true)}>aqui</IFrameLink>
              .
            </Alert>
          </Grid>
        )}

        {error && (
          <Grid item xs={12}>
            <Alert severity="error">{error}</Alert>
          </Grid>
        )}

        <Grid item xs={12}>
          <Button onClick={onSaveLink} variant="contained" color="secondary">
            Salvar
          </Button>
        </Grid>

        <PreviewDialog
          open={showIFrame}
          onClose={onClosePreview}
          onSubmit={handleVariableSubmit}
          onChange={e => setVariable(e.target.value)}
          link={link}
          displayPreview={displayPreview}
          variable={variable}
          linkWithVariable={linkWithVariable}
        />
      </ContentGrid>
    </Dialog>
  );
};

LinksDialog.defaultProps = {
  values: null
};

LinksDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  values: PropTypes.shape()
};

export default LinksDialog;
